import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/* ------------------------------------------------------------------ *
 * BWF · Group list item — "Match Card" styling. Same props & nav.
 * Accent colour is derived deterministically from the group id so each
 * pool gets a stable, distinct monogram + spine.
 * ------------------------------------------------------------------ */

const ORANGE = '#ff9800';
const LINE = '1px solid rgba(255,255,255,0.08)';
const LINE2 = '1px solid rgba(255,255,255,0.14)';

// Stable hue (0–360) from the group id/name.
const hueFrom = (group) => {
  const s = String(group.id ?? group.name ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
};

function GroupListItem({ group }) {
  const navigate = useNavigate();
  const hue = hueFrom(group);
  const accent = `oklch(0.7 0.13 ${hue})`;
  const monogram = (group.name || '?').slice(0, 2).toUpperCase();
  // num_events / open_events are optional — gracefully hidden if absent.
  const openEvents = group.num_events ?? group.open_events ?? null;

  return (
    <Box
      onClick={() => navigate(`details/${group.id}`)}
      sx={{
        position: 'relative', cursor: 'pointer', overflow: 'hidden',
        background: '#1e1e1e', border: LINE, borderRadius: '16px',
        p: '16px 16px 16px 18px', mb: 1.5,
        transition: 'border-color .15s ease, background .15s ease, transform .1s ease',
        '&:hover': { borderColor: 'rgba(255,152,0,0.5)', background: '#242424' },
        '&:active': { transform: 'scale(0.995)' },
      }}
    >
      {/* accent spine */}
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: accent, opacity: 0.9 }} />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 46, height: 46, borderRadius: '12px', flex: '0 0 auto',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: '"Archivo", Inter, sans-serif', fontWeight: 800, fontSize: 19, color: '#fff',
            background: `linear-gradient(150deg, oklch(0.5 0.12 ${hue}), oklch(0.38 0.1 ${hue}))`,
            boxShadow: `0 0 0 1px ${accent}55`,
          }}>{monogram}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{group.name}</Typography>
            {group.location && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, mt: 0.5, color: '#b0b0b0' }}>
                <LocationOnIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 500 }}>{group.location}</Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.625, px: 1.25, py: '5px', borderRadius: 999, flex: '0 0 auto',
          background: 'rgba(255,255,255,0.06)', border: LINE2,
        }}>
          <PeopleIcon sx={{ fontSize: 15, color: ORANGE }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{group.num_members}</Typography>
        </Box>
      </Box>

      {group.description && (
        <Typography sx={{
          fontSize: 13.5, color: '#b0b0b0', lineHeight: 1.5, mt: 1.5, textWrap: 'pretty',
        }}>{group.description}</Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.75, pt: 1.5, borderTop: LINE }}>
        {openEvents !== null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: openEvents > 0 ? ORANGE : '#7a7a7a' }}>
            {openEvents > 0 && (
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%', background: ORANGE,
                animation: 'bwfPulse 1.4s ease-in-out infinite',
                '@keyframes bwfPulse': { '0%,100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.35, transform: 'scale(0.7)' } },
              }} />
            )}
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              {openEvents > 0 ? `${openEvents} open event${openEvents === 1 ? '' : 's'}` : 'No open events'}
            </Typography>
          </Box>
        ) : <span />}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.375, color: '#fff' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>View</Typography>
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </Box>
      </Box>
    </Box>
  );
}

export default GroupListItem;
